import React, { useCallback, useMemo, useState } from 'react';

import ContextBar from '../components/common/ContextBar';
import TagManager from '../components/tags/TagManager';
import PrivateTagCollectionsModal from '../components/tags/PrivateTagCollectionsModal';
import useActiveTagMenu from '../components/tags/useActiveTagMenu';
import { useTags } from '../contexts/TagsContext';
import { useOptionalPreferences } from '../contexts/PreferencesContext';
import { createTag, deleteTag, getMyTags, mergeTag, restoreTag, updateTag } from '../services/tags';

/**
 * /account/tags — Account › Tags: my PRIVATE tags (docs/plans/tags.md §5.2).
 * The same manager as Settings › Tags (components/tags/TagManager), over the
 * list contexts/TagsContext holds for the whole app, with no Restricted —
 * restricted is a collection tag's. Everything applies at once.
 *
 * A private tag is its owner's alone: nobody else sees it, not a collection's
 * managers, not an admin, and it never reaches History. It needs only READ
 * access to what it tags (T4b puts it on items).
 *
 * PUT ON COLLECTIONS… (ruled b53) in a tag's ⋯ (Record): the collections
 * the tag is on — Home's `collectionGroups`, the one read of the private
 * store by collection — and putting it on or taking it off one
 * (components/tags/PrivateTagCollectionsModal). ⚑ The rows themselves do not
 * list the collections (that would read Home on every visit here); the
 * window does.
 */
export default function AccountTags() {
	const { tags, loading, error, replaceTags, refresh } = useTags();
	// Each private tag's ⋯ turns it into the active tag, or stops it (b52).
	const activeMenu = useActiveTagMenu();
	// A delete turns the active tag off and a merge moves it (keepr-api): read
	// the preference again after a change here, so the shell says what is true.
	const preferences = useOptionalPreferences();
	const ensurePreferences = preferences?.ensureLoaded;
	const onChanged = useCallback(() => {
		if (ensurePreferences) ensurePreferences({ force: true });
	}, [ensurePreferences]);

	const [putting, setPutting] = useState(null); // the tag whose collections are open

	// A private tag's own verbs: Put on collections… (b53), and Make active… /
	// Stop adding it (the active tag, b52), each in its group.
	const extraActions = useCallback(tag => [{
		key: 'collections',
		group: 'record',
		glyph: 'collection',
		label: 'Put on collections…',
		onClick: () => setPutting(tag),
	}, ...activeMenu.actionsFor(tag)], [activeMenu]);

	const service = useMemo(() => ({
		create: fields => createTag('me', fields),
		update: (id, patch) => updateTag(id, patch),
		remove: (id, options) => deleteTag(id, options),
		restore: id => restoreTag(id),
		listDeleted: () => getMyTags({ status: 'deleted' }),
		merge: (id, into, options) => mergeTag(id, into, options),
	}), []);

	return (
		<>
			{/* It names no record, so there is no chip: a plain title under the
			    one breadcrumb segment above it. */}
			<ContextBar
				breadcrumb={[{ label: 'Account', to: '/account' }]}
				title="Private tags"
			/>
			<div className="settings-column account-tags">
				<section className="form-panel">
					<div className="micro-label form-panel__kicker">Only you see these</div>
					<p className="form-panel__hint">
						Private tags are yours alone. Nobody else sees them, and they never appear in History,
						shared filters or rules.
					</p>
					<TagManager
						tags={tags}
						loading={loading}
						error={error}
						onRetry={refresh}
						canManage
						privateTags
						service={service}
						onReplace={replaceTags}
						addButtonType="primary"
						label="Private tags"
						emptyText="No private tags yet. Add one to start."
						extraActions={extraActions}
						onChanged={onChanged}
					/>
					{activeMenu.dialog}
				</section>
			</div>
			{putting ? (
				<PrivateTagCollectionsModal open onClose={() => setPutting(null)} tag={tags.find(tag => tag._id === putting._id) || putting} />
			) : null}
		</>
	);
}
